/**
 * Job actions. Only what the backend really does is clickable.
 *
 * - Stop run / Cancel job (`POST /jobs/{id}/cancel`).
 *   A queued job is cancelled at once and never starts. A job on a
 *   runner (running, or waiting on a person) gets a stop request: the
 *   runner sees it within a few seconds, closes the browser, releases
 *   its portal login and the job becomes cancelled. It is never
 *   replayed. Until the runner confirms, the screen says "Stopping".
 * - Take control lives in the Live view panel, next to the screen it
 *   controls.
 * - Pause has no endpoint.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { History, Square } from "lucide-react";

import { isFinished, type JobDetail } from "./jobApi";
import { useStopJob } from "./liveApi";
import { ErrorNotice, Panel } from "./parts";
import { BTN, BTN_DANGER, isStatus } from "./ui";

function stopError(error: unknown, queued: boolean): string {
  if (isStatus(error, 403)) {
    return "You don't have access to stop jobs. This needs the supervisor or entity admin role.";
  }
  if (isStatus(error, 409)) return "This job has already finished.";
  if (isStatus(error, 404)) return "Job not found.";
  if (isStatus(error, 429)) return "Too many requests. Wait a minute, then try again.";
  return queued ? "The job was not cancelled. Try again." : "The stop request was not sent. Try again.";
}

export function JobControls({
  job,
  stopRequested = false,
}: {
  job: JobDetail;
  /** A stop has already been requested (from this screen or another). */
  stopRequested?: boolean;
}) {
  const stop = useStopJob(job.id);
  const [confirming, setConfirming] = useState(false);
  const finished = isFinished(job.status);
  const queued = job.status === "queued";
  const stopping = !finished && !queued && (stopRequested || stop.data?.status === "stopping");

  return (
    <Panel title="Actions">
      <div className="space-y-3 text-sm">
        <Link to={`/jobs/${job.id}/replay`} className={BTN}>
          <History size={12} aria-hidden /> Open replay
        </Link>

        {!finished && stopping && (
          <p role="status" className="rounded-sm border border-paper-line-strong bg-paper-3 p-3 text-ink-1">
            Stopping. The runner has been told to stop and will close the browser within a few
            seconds. This page updates when it has.
          </p>
        )}

        {!finished && !stopping && !confirming && (
          <div>
            <button type="button" className={BTN_DANGER} onClick={() => setConfirming(true)}>
              <Square size={12} aria-hidden /> {queued ? "Cancel job" : "Stop run"}
            </button>
            <p className="mt-1 text-xs text-ink-3">
              {queued
                ? "The job is cancelled and does not start."
                : "Stops the runner within a few seconds and closes its browser. The job is not retried."}
            </p>
          </div>
        )}

        {!finished && !stopping && confirming && (
          <div
            role="alertdialog"
            aria-label={queued ? "Confirm cancel" : "Confirm stop"}
            className="rounded-sm border border-status-failed/40 p-3"
          >
            <p className="text-xs text-ink-1">
              {queued
                ? "Cancel this queued job? It cannot be restarted."
                : "Stop this run now? The runner closes the browser wherever it is, including part-way through a step. The job ends and cannot be resumed."}
            </p>
            <div className="mt-2 flex gap-2">
              <button
                type="button"
                className={BTN_DANGER}
                disabled={stop.isPending}
                onClick={() => stop.mutate(undefined, { onSettled: () => setConfirming(false) })}
              >
                {queued ? "Confirm cancel" : "Stop run now"}
              </button>
              <button
                type="button"
                className={BTN}
                disabled={stop.isPending}
                onClick={() => setConfirming(false)}
              >
                {queued ? "Keep job" : "Keep running"}
              </button>
            </div>
          </div>
        )}

        {stop.isError && <ErrorNotice message={stopError(stop.error, queued)} error={stop.error} />}
      </div>
    </Panel>
  );
}
